import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { DateField } from "@/components/DateField";
import { api, errMsg } from "@/lib/api";
import { Loader2 } from "lucide-react";

export const MarkRenewedDialog = ({ open, onOpenChange, item, onSaved }) => {
  const [mode, setMode] = useState("next"); // next | archive
  const [nextDate, setNextDate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMode("next");
      setNextDate("");
    }
  }, [open]);

  const submit = async () => {
    if (mode === "next" && !nextDate) {
      toast.error("Pick the next due date, or choose to archive the item.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/items/${item.id}/renew`, {
        new_due_date: mode === "next" ? nextDate : null,
      });
      toast.success(mode === "next" ? "Renewed — the next reminder cycle is set." : "Renewed and archived.");
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Mark renewed</DialogTitle>
          <DialogDescription>
            Nice work. What happens next for “{item?.name}”?
          </DialogDescription>
        </DialogHeader>
        <RadioGroup value={mode} onValueChange={setMode} className="space-y-1">
          <label className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${mode === "next" ? "border-foreground/30 bg-secondary/60" : "hover:bg-muted/50"}`}>
            <RadioGroupItem value="next" className="mt-0.5" data-testid="renew-mode-next-radio" />
            <div className="space-y-2 flex-1">
              <div>
                <p className="text-sm font-medium">It renews again — set the next due date</p>
                <p className="text-xs text-muted-foreground mt-0.5">Reminders continue on the new cycle.</p>
              </div>
              {mode === "next" && (
                <DateField value={nextDate} onChange={setNextDate} testId="renew-next-date-field" />
              )}
            </div>
          </label>
          <label className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${mode === "archive" ? "border-foreground/30 bg-secondary/60" : "hover:bg-muted/50"}`}>
            <RadioGroupItem value="archive" className="mt-0.5" data-testid="renew-mode-archive-radio" />
            <div>
              <p className="text-sm font-medium">It's done — archive this item</p>
              <p className="text-xs text-muted-foreground mt-0.5">No more reminders. It stays in your records.</p>
            </div>
          </label>
        </RadioGroup>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-xl" data-testid="renew-confirm-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm renewal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
